
import { useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const testimonials = [
  {
    id: 1,
    quote: "Working with Unuu transformed our entire approach to social media. They truly understand how to connect brands with their audience.",
    name: "Sarah Mitchell",
    role: "Founder, Luxe Botanica",
    avatar: "https://public.readdy.ai/ai/img_res/c18c5259b34740fe3d0341052a4e8df1.jpg",
    rating: 5
  },
  {
    id: 2,
    quote: "The team at Unuu brought creativity and strategy together perfectly. Our bookings increased by 180% in just six months.",
    name: "James Crawford",
    role: "GM, The Harbour Hotel",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20distinguished%20male%20hotel%20manager%20in%20his%2040s%20wearing%20suit%20with%20confident%20expression%20against%20elegant%20neutral%20background%20corporate%20portrait&width=100&height=100&seq=test2&orientation=squarish",
    rating: 5
  },
  {
    id: 3,
    quote: "They don't just post content—they build communities. Our members feel more connected to our brand than ever before.",
    name: "Emma Chen",
    role: "Owner, FitFlow Studio",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20fit%20female%20yoga%20instructor%20in%20her%2030s%20with%20serene%20expression%20and%20natural%20look%20against%20soft%20light%20background%20wellness%20portrait&width=100&height=100&seq=test3&orientation=squarish",
    rating: 5
  },
  {
    id: 4,
    quote: "Unuu helped us tell our sustainability story in a way that resonated with our target audience. Sales grew 320% year over year.",
    name: "Olivia Hart",
    role: "CEO, Velvet & Thread",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20a%20stylish%20female%20fashion%20entrepreneur%20in%20her%2030s%20with%20modern%20look%20and%20confident%20smile%20against%20minimal%20background%20creative%20portrait&width=100&height=100&seq=test4&orientation=squarish",
    rating: 5
  }
];

export default function ClientTestimonials() {
  const { ref, isVisible } = useScrollAnimation();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 420;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`flex flex-col md:flex-row md:items-end md:justify-between mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <span className="inline-block px-4 py-1.5 bg-[#00989e]/10 text-[#00989e] text-sm font-medium rounded-full mb-4">
              Client Love
            </span>
            <h2 className="font-['Montserrat'] text-3xl md:text-4xl font-bold text-slate-900">
              What Our Clients Say
            </h2>
          </div>
          <div className="flex gap-3 mt-6 md:mt-0">
            <button
              onClick={() => scroll('left')}
              className="w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-slate-700"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line text-slate-700"></i>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface Testimonial {
  id: number;
  quote: string;
  name: string;
  role: string;
  avatar: string;
  rating: number;
}

function TestimonialCard({ testimonial, index }: { testimonial: Testimonial; index: number }) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`flex-shrink-0 w-[380px] bg-white rounded-2xl p-8 shadow-sm snap-start transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="flex gap-1 mb-6">
        {[...Array(testimonial.rating)].map((_, i) => (
          <i key={i} className="ri-star-fill text-amber-400"></i>
        ))}
      </div>

      <p className="text-slate-600 mb-8 leading-relaxed">
        &quot;{testimonial.quote}&quot;
      </p>

      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full overflow-hidden">
          <img
            src={testimonial.avatar}
            alt={testimonial.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div>
          <p className="font-semibold text-slate-900">{testimonial.name}</p>
          <p className="text-sm text-slate-500">{testimonial.role}</p>
        </div>
      </div>
    </div>
  );
}
